import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SmartImage from "@/components/SmartImage";

const STATUS_STYLE = {
  paid: "bg-sage/20 text-sage",
  complete: "bg-sage/20 text-sage",
  initiated: "bg-linen text-muted_ink",
  open: "bg-linen text-muted_ink",
  expired: "bg-terracotta/10 text-terracotta",
  failed: "bg-terracotta/10 text-terracotta",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paidOnly, setPaidOnly] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders", { params: { paid_only: paidOnly } });
      setOrders(data);
    } catch (err) {
      console.error("Failed to load orders", err);
    } finally {
      setLoading(false);
    }
  }, [paidOnly]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="orders-page" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="label-eyebrow text-sage mb-3">Account</div>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tight">Order history.</h1>
      <p className="text-muted_ink mt-4 max-w-xl">Every Folio transaction tied to your account, in order.</p>

      <div className="mt-10 flex items-center justify-between border-b border-edge pb-4">
        <div className="flex gap-2">
          <button
            data-testid="filter-paid"
            onClick={() => setPaidOnly(true)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
              paidOnly
                ? "bg-ink text-paper border-ink"
                : "border-edge text-muted_ink hover:border-ink hover:text-ink"
            }`}
          >
            Paid only
          </button>
          <button
            data-testid="filter-all"
            onClick={() => setPaidOnly(false)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
              !paidOnly
                ? "bg-ink text-paper border-ink"
                : "border-edge text-muted_ink hover:border-ink hover:text-ink"
            }`}
          >
            All transactions
          </button>
        </div>
        <div className="text-xs text-muted_ink">
          {orders.length} {orders.length === 1 ? "order" : "orders"}
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-muted_ink">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="border border-edge p-16 text-center">
            <div className="font-serif text-3xl italic mb-3">
              {paidOnly ? "No paid orders yet." : "No transactions yet."}
            </div>
            <p className="text-muted_ink mb-6">
              {paidOnly
                ? "Once you complete a purchase, your receipts will appear here."
                : "Nothing to show — try browsing the shelf."}
            </p>
            <Link to="/browse" className="btn-primary">Browse the shelf</Link>
          </div>
        ) : (
          <div className="border border-edge bg-white">
            <div className="grid grid-cols-12 px-6 py-3 label-eyebrow border-b border-edge">
              <div className="col-span-5">Book</div>
              <div className="col-span-3">Date</div>
              <div className="col-span-2">Amount</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {orders.map((o) => (
              <div
                key={o.id}
                data-testid={`order-row-${o.id}`}
                className="grid grid-cols-12 px-6 py-5 border-b border-edge text-sm items-center"
              >
                <div className="col-span-5 flex items-center gap-4 min-w-0">
                  {o.cover && (
                    <SmartImage src={o.cover} alt="" className="w-10 aspect-[3/4] object-cover flex-shrink-0 bg-linen" containerClassName="w-10 !aspect-[3/4] flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <Link
                      to={`/book/${o.book_id}`}
                      className="font-serif text-lg block truncate hover:text-terracotta"
                    >
                      {o.book_title || "Untitled"}
                    </Link>
                    <div className="text-xs text-muted_ink truncate">#{o.id.slice(-8)}</div>
                  </div>
                </div>
                <div className="col-span-3 text-muted_ink">{formatDate(o.created_at)}</div>
                <div className="col-span-2 font-medium">${o.amount?.toFixed(2)}</div>
                <div className="col-span-2 text-right">
                  <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest ${
                    STATUS_STYLE[o.payment_status] || "bg-linen text-muted_ink"
                  }`}>
                    {o.payment_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
